import axios from 'axios';
import configService from '../services/configService';

// Configure axios defaults
axios.defaults.withCredentials = false; // Changed to false for Auth0 JWT tokens

// Initialize axios with dynamic configuration
const initializeAxios = async () => {
  try {
    const config = await configService.getConfig();
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      // When running locally, always point to the local backend
      // (endpoint paths already include /api/ prefix, so don't add it here)
      axios.defaults.baseURL = 'http://localhost:3006';
      console.log('✅ Axios configured for local development:', axios.defaults.baseURL);
    } else {
      // Production: use config or fallback to same origin
      axios.defaults.baseURL = config.api.baseUrl || config.api.url || window.location.origin;
      console.log('✅ Axios configured with API URL:', axios.defaults.baseURL);
    }
  } catch (error) {
    console.warn('⚠️ Failed to load API config, using same-origin as fallback');
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    axios.defaults.baseURL = isLocalhost
      ? 'http://localhost:3006'
      : window.location.origin;
  }
};

// Call initialization
initializeAxios();

// Global token storage
let authToken = null;

// Function to set the auth token
export const setAuthToken = (token) => {
  authToken = token;
};

// Function to clear the auth token
export const clearAuthToken = () => {
  authToken = null;
};

// Add request interceptor to include Auth0 JWT token
axios.interceptors.request.use(
  (config) => {
    // Add Authorization header if token is available
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
      console.log('🔑 Sending request with token:', config.url, authToken.substring(0, 20) + '...');
    } else {
      console.log('⚠️ No token available for request:', config.url);
      
      // For protected API endpoints without tokens, redirect to login instead of blocking
      if (config.url && config.url.includes('/api/') && !config.url.includes('/api/config')) {
        console.log('🔄 API request without token - will redirect to login if 401 response');
        // Don't block the request here - let it proceed and handle 401 in response interceptor
        // This allows the natural auth flow to work properly
      }
    }

    // Add cache control headers for GET requests
    if (config.method === 'get') {
      config.headers = {
        ...config.headers,
        'Cache-Control': 'no-cache'
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors globally
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors globally
    if (error.response?.status === 401) {
      console.error('❌ Authentication required - redirecting to login');
      
      // Clear any existing token
      clearAuthToken();
      
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        console.log('🔄 Redirecting to login page');
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      console.error('❌ Access denied - insufficient permissions');
    }
    
    return Promise.reject(error);
  }
);

export default axios;
